import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IClient } from '@techspert-io/clients';
import {
  combineLatest,
  debounceTime,
  startWith,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { MaterialModule } from '../../../../shared/patterns/material-module';
import { SentenceCasePipe } from '../../../../shared/pipes/sentence-case.pipe';
import { EnrichedUser, UserGroupKey } from '../../models/user.models';
import { UserGroupPipe } from '../../pipes/user-group.pipe';

export enum UserStatus {
  All = 'all',
  Active = 'active',
  Inactive = 'inactive',
}

type UsersFilterForm = FormGroup<{
  search: FormControl<string>;
  groupIds: FormControl<string[]>;
  clientIds: FormControl<string[]>;
  status: FormControl<UserStatus>;
}>;

export type UsersFilterFormValue = Partial<{
  search: string;
  groupIds: string[];
  clientIds: string[];
  status: UserStatus;
}>;

@Component({
  selector: 'app-users-filter',
  templateUrl: './users-filter.component.html',
  styleUrls: ['./users-filter.component.scss'],
  imports: [MaterialModule, UserGroupPipe, SentenceCasePipe],
})
export class UsersFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() users: EnrichedUser[] = [];
  @Input() clients: IClient[] = [];

  @Output() usersFilterChangeSignal = new EventEmitter<UsersFilterFormValue>();

  showAdditionalFilters = false;

  groupOptions: {
    user_group_id: string;
    key: UserGroupKey;
  }[] = [];

  userStatuses = Object.values(UserStatus);

  form: UsersFilterForm = new FormGroup({
    search: new FormControl(''),
    groupIds: new FormControl([]),
    clientIds: new FormControl([]),
    status: new FormControl<UserStatus>(UserStatus.Active),
  });

  private destroy$ = new Subject();

  ngOnInit() {
    const searchChange$ = this.form.controls.search.valueChanges.pipe(
      startWith(''),
      debounceTime(250)
    );

    const clientIdsChange$ = this.form.controls.clientIds.valueChanges.pipe(
      startWith([])
    );

    const groupIdsChange$ = this.form.controls.groupIds.valueChanges.pipe(
      startWith([])
    );

    const statusChange$ = this.form.controls.status.valueChanges.pipe(
      startWith(UserStatus.Active)
    );

    combineLatest([
      searchChange$,
      clientIdsChange$,
      groupIdsChange$,
      statusChange$,
    ])
      .pipe(
        tap(([search, clientIds, groupIds, status]) =>
          this.usersFilterChangeSignal.emit({
            search,
            clientIds,
            groupIds,
            status,
          })
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.users) {
      return;
    }

    this.groupOptions = Object.entries(
      this.users.reduce<Record<string, UserGroupKey>>(
        (groups, { user_groups }) => ({
          ...groups,
          ...Object.fromEntries(
            user_groups.map(({ user_group_id, key }) => [user_group_id, key])
          ),
        }),
        {}
      )
    ).map(([user_group_id, key]) => ({
      user_group_id,
      key,
    }));
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  toggleAdditionalFilters() {
    this.showAdditionalFilters = !this.showAdditionalFilters;
  }
}
